import classNames from "classnames";
import React, { useEffect, useRef, useState } from "react";
import { CouponItemInterface, StoreInterface, WABotSession } from "@/assets/interfaces";
import usePreventFirstRun from "@/components/hooks/usePreventFirstRun";
import Portal from "@/components/portal";
import CheckoutModal from "@/components/storefronts-v2/modals/checkout";
import SelectVariantModal from "@/components/storefronts-v2/modals/select-variant";
import { useAppendScript } from "@/components/hooks/useAppendScript";
import CustomerCartInfo from "@/components/storefronts-v2/modals/customer-cart-info";
import { useModals } from "@/components/hooks/useModals";
import { computePaymentCouponDiscount } from "@/assets/js/utils/utils";
import { toast } from "@/components/ui/toast";
import CartContextV2 from "@/contexts/cart-context-v2";

interface Props {
  storeIsOpen?: boolean;
  phoneNumber: string;
  store: StoreInterface;
  productPage?: boolean;
  botSession?: WABotSession;
  openCart?: boolean;
}

const NewStoreCart: React.FC<Props> = ({ openCart, botSession, store, productPage }) => {
  const {
    cart,
    toggleModal,
    modals,
    currentProduct,
    addOrRemoveItem,
    cartIsFromServer,
    setCartIsFromServer,
    formatAsCurrency,
    downloadCart,
    priceUnformatted,
    currencies,
  } = CartContextV2.useContainer();
  const cartLengthRef = useRef<number>(0);
  const [showBanner, setShowBanner] = useState(false);
  const couponModal = useModals(["coupon"]);
  const [coupon, setCoupon] = useState<{ couponCode: string; discountAmount: number }>(null);
  const couponDataCache = useRef<CouponItemInterface>(null);
  useEffect(() => {
    if (couponDataCache.current && priceUnformatted > 0) {
      applyCoupon(couponDataCache.current);
    }
  }, [couponDataCache]);

  useEffect(() => {
    if (openCart) toggleModal("cart");
  }, [openCart]);

  useEffect(() => {
    if (botSession?.cart_id) downloadCart(botSession.cart_id);
  }, [botSession]);

  useAppendScript(
    `https://maps.googleapis.com/maps/api/js?key=${process.env.NEXT_PUBLIC_GOOGLE_API_KEY}&libraries=places&callback=initMap&solution_channel=GMP_QB_addressselection_v1_cA`,
    "GOOGLE_PLACE_SCRIPT"
  );

  useEffect(() => {
    (window as any).initMap = (e) => {
      console.log(e, "Google places loaded");
    };
  }, []);

  useEffect(() => {
    if (cartIsFromServer) {
      cartLengthRef.current = cart.length;
      return;
    }

    if (cart.length > cartLengthRef.current) {
      setShowBanner(true);

      setTimeout(() => {
        setShowBanner(false);
      }, 2500);
    }

    cartLengthRef.current = cart.length;
  }, [cart, cartIsFromServer]);

  const applyCoupon = (paymentCoupon: CouponItemInterface) => {
    // const total = priceUnformatted;
    couponDataCache.current = paymentCoupon;
    const discountAmount = computePaymentCouponDiscount(priceUnformatted, paymentCoupon);
    setCoupon({ couponCode: paymentCoupon.coupon_code, discountAmount });
    toast.success({
      title: "Coupon successfully applied",
      message: `Coupon (${paymentCoupon.coupon_code}) has been sucessfully added`,
    });

    couponModal.toggleModal("coupon");
  };

  return (
    <>
      <div
        className={classNames(
          "fixed top-0 left-0 w-full bg-success flex items-center text-white font-medium text-1xs sm:text-sm justify-center py-3 px-3 transition-all ease-out duration-200 transform z-[9999]",
          { "-translate-y-full opacity-0": !showBanner, "translate-y-0 opacity-100": showBanner }
        )}
      >
        Item added to cart
      </div>
      <Portal>
        <CheckoutModal
          show={modals.cart}
          couponModal={couponModal}
          applyCoupon={applyCoupon}
          coupon={coupon}
          removeCoupon={() => setCoupon(null)}
          toggle={() => toggleModal("cart")}
          store={store}
        />
        {currentProduct && (
          <SelectVariantModal show={modals.variants} toggle={() => toggleModal("variants")} product={currentProduct} />
        )}
        <CustomerCartInfo show={modals.customer_modal} toggle={() => toggleModal("customer_modal")} />
      </Portal>
    </>
  );
};

export default NewStoreCart;
